import { Badge, DatePicker, Input, Modal, Popover, Select, Spin, Table, Tabs, message } from "antd";
import { SearchOutlined } from "@mui/icons-material";
import {
  InfoCircleFilled,
  BarChartOutlined,
  ExportOutlined,
  EyeOutlined,
  CalendarOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import COLORS from "common/Colors";
import { ButtonFilled } from "reusableComponent/UIButtons";
import TextLink from "./TextLink";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import {
  getUserInfo,
  getMenuList,
  getUserRoleId,
  getUserList,
  getAllDepartmentList,
} from "redux/selectors";
import {
  APPRAISAL_MINE_STATUS,
  APPRAISAL_MINE_ACTIONS,
  APPRAISAL_MYTEAM_STATUS,
  APPRAISAL_MYTEAM_ACTIONS,
  statusMeta,
} from "./statusTag";
import PerformanceMatrix from "./PerformanceMatrix";
import { DEFAULT_SCORE_WEIGHTAGE, weightedAverage } from "./WeightageSettingsModal";
import AdjustDatesModal from "./AdjustDatesModal";

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  .filters {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  .ant-input-affix-wrapper,
  .ant-input,
  .ant-picker,
  .ant-select .ant-select-selector {
    border-radius: 24px !important;
  }

  .ant-input::placeholder,
  .ant-picker-input > input::placeholder,
  .ant-select-selection-placeholder {
    color: #9ca3af !important;
  }
`;

const StyledTable = styled(Table)`
  .ant-table {
    border: 1px solid #ccc;
    border-radius: 12px;
    overflow: hidden;
  }
  .ant-table-container {
    border-radius: 12px;
  }
  .ant-table-content {
    overflow-x: auto;
  }
  .ant-table-cell {
    padding: 16px;
    white-space: nowrap;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: 600;
    color: ${COLORS.SECONDARY_BLACK};
  }
`;

const DraftBadge = styled.span`
  background: #262626;
  color: #fff;
  border-radius: 12px;
  padding: 2px 12px;
  font-size: 12px;
  margin-left: 10px;
`;

const ActionWrapper = styled.div`
  opacity: 0;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: center;
  gap: 14px;
  justify-content: flex-end;

  .ant-table-row:hover & {
    opacity: 1;
  }

  .action-icon {
    cursor: pointer;
    color: ${COLORS.PRIMARY};
    font-size: 16px;
  }
`;

const ICONS = {
  calendar: CalendarOutlined,
  delete: DeleteOutlined,
  chart: BarChartOutlined,
};

const MineRowActions = ({ status, onAction }) => {
  const action = APPRAISAL_MINE_ACTIONS[status];
  if (!action) return null;

  if (action.type === "info") {
    return (
      <ActionWrapper>
        <Popover content={action.tooltip}>
          <InfoCircleFilled className="action-icon" />
        </Popover>
      </ActionWrapper>
    );
  }

  const LinkIcon = action.icon === "eye" ? EyeOutlined : ExportOutlined;

  return (
    <ActionWrapper>
      {action.extraIcon && (
        <Badge dot>
          <BarChartOutlined className="action-icon" onClick={(e) => onAction(e, "chart")} />
        </Badge>
      )}
      <TextLink onClick={(e) => onAction(e, "primary")}>
        <LinkIcon /> {action.label}
      </TextLink>
    </ActionWrapper>
  );
};

const MyTeamRowActions = ({ status, onAction }) => {
  const action = APPRAISAL_MYTEAM_ACTIONS[status];
  if (!action) return null;

  return (
    <ActionWrapper>
      {action.type === "link+icons" && (
        <TextLink onClick={(e) => onAction(e, "primary")}>{action.label}</TextLink>
      )}
      {action.icons.map((iconKey) => {
        const Icon = ICONS[iconKey];
        return (
          <Icon key={iconKey} className="action-icon" onClick={(e) => onAction(e, iconKey)} />
        );
      })}
    </ActionWrapper>
  );
};

const PERIOD_OPTIONS = [
  { label: "Yearly", value: "Yearly" },
  { label: "Half Yearly", value: "Half Yearly" },
  { label: "Quarterly", value: "Quarterly" },
  { label: "Probationary", value: "Probationary" },
];

const formatDate = (value) => (value ? dayjs(value).format("Do MMM YYYY") : "-");

const formatTooltipDate = (value) =>
  value && dayjs(value).isValid() ? dayjs(value).format("DD-MM-YYYY") : "-";

const formatScore = (value) => (typeof value === "number" ? value.toFixed(2) : "-");

// Consolidated matrix hover tooltip - same table layout as Insyts' matrix:
// one row per employee sitting on the hovered dot (several can share the
// exact same scores).
const MatrixTooltipTable = styled(Table)`
  width: 640px;
  max-width: 80vw;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  overflow: hidden;

  .ant-table-thead > tr > th {
    background: #f5f5f5;
    font-weight: 600;
    color: ${COLORS.SECONDARY_BLACK};
    white-space: nowrap;
  }
  .ant-table-tbody > tr > td {
    color: #4b5563;
  }
`;

const MATRIX_TOOLTIP_COLUMNS = [
  { title: "Name", dataIndex: "fullname", key: "fullname", render: (v) => v || "-" },
  { title: "Department", dataIndex: "department", key: "department", render: (v) => v || "-" },
  { title: "Attended on", dataIndex: "attendedon", key: "attendedon", render: formatTooltipDate },
  { title: "Soft skill", dataIndex: "x", key: "x", render: formatScore },
  { title: "Technical skill", dataIndex: "y", key: "y", render: formatScore },
];

const MatrixTooltip = ({ points }) => (
  <MatrixTooltipTable
    columns={MATRIX_TOOLTIP_COLUMNS}
    dataSource={points}
    rowKey="id"
    pagination={false}
    size="middle"
  />
);

const averageOf = (values) => {
  const nums = values.filter((val) => typeof val === "number");
  if (!nums.length) return null;
  return Math.round((nums.reduce((sum, val) => sum + val, 0) / nums.length) * 10) / 10;
};

// Technical Skills (Y axis) comes from the main Planning scoring (Financial /
// Customer / Internal Business / Learning & Growth / Rollover); Soft Skills
// (X axis) comes from the Individual (Soft Skills) scoring - each perspective
// prefers the appraiser/appraisee-agreed score once one exists, falling back
// to the appraiser score before that.
const computeMatrixAxes = (appraisal) => {
  const technical = averageOf(
    (appraisal?.scoring || []).map((item) => item.agreedscore ?? item.appraiserscore)
  );
  const soft = averageOf(
    (appraisal?.softskillsscoring || []).map((item) => item.agreedscore ?? item.appraiserscore)
  );
  return { x: soft ?? 0, y: technical ?? 0 };
};

// Unrounded Technical / Soft Skills averages (same score sources as
// computeMatrixAxes) for the weighted Overall Average - rounding the inputs
// first would skew the weighted result.
const exactAverage = (values) => {
  const nums = values.filter((val) => typeof val === "number");
  return nums.length ? nums.reduce((sum, val) => sum + val, 0) / nums.length : null;
};
const computeSkillAverages = (appraisal) => ({
  technical: exactAverage(
    (appraisal?.scoring || []).map((item) => item.agreedscore ?? item.appraiserscore)
  ),
  soft: exactAverage(
    (appraisal?.softskillsscoring || []).map((item) => item.agreedscore ?? item.appraiserscore)
  ),
});

const StatusCell = ({ status, map }) => {
  const meta = statusMeta(map, status);
  return <span style={{ color: meta.color, fontWeight: 500 }}>{meta.label}</span>;
};

// "notopen" only means the appraisal window hasn't started yet - once the
// start date ("from") actually arrives it should open for the appraisee to
// attend, computed on read (same pattern as KPI Planning's checkpoint-due
// derivation) rather than relying on a backend job to flip the stored status.
// But if this cycle has progress-review checkpoints, the appraisee can't
// attend until every one of them has been closed by the appraiser - even if
// the attend date has technically arrived (e.g. it lands on the same day as
// the last checkpoint) - completeprogressreview is what actually flips
// appraisalstatus to "yettoattend" for those; this date-based fallback only
// covers contracts with no progress-review checkpoints configured at all.
const isAppraisalDue = (record) => {
  if (record.appraisalstatus !== "notopen" || !record.from) return false;
  const reviewDates = record.progressreviewdates || [];
  if (reviewDates.length > 0 && !reviewDates.every((item) => item.completed)) return false;
  return !dayjs(record.from).startOf("day").isAfter(dayjs().startOf("day"));
};

const deriveAppraisalStatus = (record) =>
  isAppraisalDue(record) ? "yettoattend" : record.appraisalstatus;

const MineAppraisal = () => {
  const history = useHistory();
  const userInfo = useSelector(getUserInfo);
  const [dataList, setDataList] = useState([]);
  const [filterList, setFilterList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState();
  const [date, setDate] = useState();
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixAxes, setMatrixAxes] = useState({ x: 0, y: 0 });

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/mineappraisal/${userInfo?.userid}`,
        "get"
      );
      setDataList(code === STATUS_CODE.SUCCESS && Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching appraisal list:", error);
      setDataList([]);
    } finally {
      setLoading(false);
    }
  };

  const openMatrix = async (appraisalid) => {
    setMatrixOpen(true);
    setMatrixLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/appraisal/${appraisalid}`,
        "get"
      );
      setMatrixAxes(code === STATUS_CODE.SUCCESS && data ? computeMatrixAxes(data) : { x: 0, y: 0 });
    } catch (error) {
      console.error("Error fetching appraisal for performance matrix:", error);
      setMatrixAxes({ x: 0, y: 0 });
    } finally {
      setMatrixLoading(false);
    }
  };

  useEffect(() => {
    if (userInfo?.userid) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.userid]);

  const mergedList = useMemo(
    () => dataList.map((record) => ({ ...record, appraisalstatus: deriveAppraisalStatus(record) })),
    [dataList]
  );

  useEffect(() => {
    let filtered = [...mergedList];
    if (search) {
      filtered = filtered.filter((item) =>
        item?.period?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (period) filtered = filtered.filter((item) => item?.period === period);
    if (date) {
      filtered = filtered.filter((item) =>
        dayjs(item?.createdon).isSame(dayjs(date), "day")
      );
    }
    setFilterList(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, period, date, mergedList]);

  const columns = [
    { title: "Period", dataIndex: "period", key: "period", render: (t) => t || "-" },
    { title: "Created on", dataIndex: "createdon", key: "createdon", render: formatDate },
    { title: "From", dataIndex: "from", key: "from", render: formatDate },
    { title: "Attended on", dataIndex: "attendedon", key: "attendedon", render: formatDate },
    {
      title: "Status",
      dataIndex: "appraisalstatus",
      key: "appraisalstatus",
      render: (status) => <StatusCell status={status} map={APPRAISAL_MINE_STATUS} />,
    },
    {
      key: "actions",
      width: 180,
      render: (_, record) => (
        <MineRowActions
          status={record.appraisalstatus}
          onAction={(e, action) => {
            e.stopPropagation();
            if (action === "chart") {
              openMatrix(record.id);
              return;
            }
            history.push("/performance-kpi-attend", { appraisalid: record.id });
          }}
        />
      ),
    },
  ];

  return (
    <div>
      <FilterBar>
        <div className="lite-text">Total Role: {dataList.length}</div>
        <div className="filters">
          <Input
            placeholder="Search"
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
          <Select
            placeholder="Period"
            allowClear
            options={PERIOD_OPTIONS}
            value={period}
            onChange={setPeriod}
            style={{ width: 140 }}
          />
          <DatePicker placeholder="Date" value={date} onChange={setDate} />
        </div>
      </FilterBar>
      <Spin spinning={loading}>
        <StyledTable
          columns={columns}
          dataSource={filterList}
          pagination={false}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          onRow={(record) => ({
            style: { cursor: record.appraisalstatus === "notopen" ? "default" : "pointer" },
            onClick: () => {
              if (record.appraisalstatus === "notopen") return;
              history.push("/performance-kpi-attend", { appraisalid: record.id });
            },
          })}
        />
      </Spin>

      <Modal
        title="Overall Performance Matrix"
        open={matrixOpen}
        onCancel={() => setMatrixOpen(false)}
        footer={null}
        width={900}
        centered
      >
        <Spin spinning={matrixLoading}>
          <PerformanceMatrix Xaxis={matrixAxes.x} Yaxis={matrixAxes.y} matrixdetails />
        </Spin>
      </Modal>
    </div>
  );
};

const MyTeamAppraisal = ({ scoreWeightage }) => {
  const history = useHistory();
  const userInfo = useSelector(getUserInfo);
  const [dataList, setDataList] = useState([]);
  const [filterList, setFilterList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState();
  const [date, setDate] = useState();
  const [roleType, setRoleType] = useState();
  const [status, setStatus] = useState();
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixAxes, setMatrixAxes] = useState({ x: 0, y: 0 });
  const [consolidatedOpen, setConsolidatedOpen] = useState(false);
  const [consolidatedLoading, setConsolidatedLoading] = useState(false);
  const [consolidatedRows, setConsolidatedRows] = useState([]);
  const [cSearch, setCSearch] = useState("");
  const [cPeriod, setCPeriod] = useState();
  const [cDate, setCDate] = useState();
  const [adjustDatesRecord, setAdjustDatesRecord] = useState(null);
  const weightage = scoreWeightage || DEFAULT_SCORE_WEIGHTAGE;
  // Closed appraisal id -> { technical, soft } averages, for Overall Average.
  // The list endpoint doesn't return scores, so closed rows are fetched one
  // by one (same as the Consolidated matrix already does).
  const [skillAverages, setSkillAverages] = useState({});
  const [averagesLoading, setAveragesLoading] = useState(false);
  const userList = useSelector(getUserList);
  const departmentList = useSelector(getAllDepartmentList);

  const departmentOf = (userid) => {
    const user = (userList || []).find((u) => u.userid === userid);
    const ids = Array.isArray(user?.basedepartmentid)
      ? user.basedepartmentid
      : user?.basedepartmentid
      ? [user.basedepartmentid]
      : [];
    return ids
      .map((id) => (departmentList || []).find((d) => String(d.department_id) === String(id)))
      .filter(Boolean)
      .map((d) => d.department_name)
      .join(", ");
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/myteamappraisal/${userInfo?.userid}`,
        "get"
      );
      const list = code === STATUS_CODE.SUCCESS && Array.isArray(data) ? data : [];
      setDataList(list);
      fetchSkillAverages(list);
    } catch (error) {
      console.error("Error fetching appraisal team list:", error);
      setDataList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSkillAverages = async (list) => {
    const closedRows = list.filter((row) => row.appraisalstatus === "closed");
    if (!closedRows.length) {
      setSkillAverages({});
      return;
    }
    setAveragesLoading(true);
    try {
      const details = await Promise.all(
        closedRows.map((row) =>
          CALL_API(`performance-kpi-management/appraisal/${row.id}`, "get").catch(() => null)
        )
      );
      const averages = {};
      closedRows.forEach((row, index) => {
        const result = details[index];
        if (result?.code === STATUS_CODE.SUCCESS && result?.data) {
          averages[row.id] = computeSkillAverages(result.data);
        }
      });
      setSkillAverages(averages);
    } catch (error) {
      console.error("Error fetching appraisal scores:", error);
      setSkillAverages({});
    } finally {
      setAveragesLoading(false);
    }
  };

  const openMatrix = async (appraisalid) => {
    setMatrixOpen(true);
    setMatrixLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/appraisal/${appraisalid}`,
        "get"
      );
      setMatrixAxes(code === STATUS_CODE.SUCCESS && data ? computeMatrixAxes(data) : { x: 0, y: 0 });
    } catch (error) {
      console.error("Error fetching appraisal for performance matrix:", error);
      setMatrixAxes({ x: 0, y: 0 });
    } finally {
      setMatrixLoading(false);
    }
  };

  // Consolidated matrix plots every closed appraisal in this manager's team
  // as its own dot - only closed appraisals carry real appraiser/agreed
  // scores, so unclosed rows are skipped rather than plotted at (0, 0).
  const openConsolidated = async () => {
    setConsolidatedOpen(true);
    setConsolidatedLoading(true);
    try {
      const closedRows = dataList.filter((row) => row.appraisalstatus === "closed");
      const details = await Promise.all(
        closedRows.map((row) =>
          CALL_API(`performance-kpi-management/appraisal/${row.id}`, "get").catch(() => null)
        )
      );
      const rows = closedRows.map((row, index) => {
        const result = details[index];
        const appraisal = result?.code === STATUS_CODE.SUCCESS ? result?.data : null;
        const axes = appraisal ? computeMatrixAxes(appraisal) : { x: 0, y: 0 };
        // Extra employee details for the dot's hover tooltip.
        return {
          ...row,
          ...axes,
          appraiseeuserid: appraisal?.appraiseeuserid,
          department: departmentOf(appraisal?.appraiseeuserid),
        };
      });
      setConsolidatedRows(rows);
    } catch (error) {
      console.error("Error fetching consolidated performance matrix:", error);
      setConsolidatedRows([]);
    } finally {
      setConsolidatedLoading(false);
    }
  };

  const consolidatedFiltered = consolidatedRows.filter((row) => {
    if (cSearch && !row.fullname?.toLowerCase().includes(cSearch.toLowerCase())) return false;
    if (cPeriod && row.period !== cPeriod) return false;
    if (cDate && !dayjs(row.createdon).isSame(dayjs(cDate), "day")) return false;
    return true;
  });

  useEffect(() => {
    if (userInfo?.userid) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.userid]);

  useEffect(() => {
    let filtered = [...dataList];
    if (search) {
      filtered = filtered.filter((item) =>
        item?.fullname?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (period) filtered = filtered.filter((item) => item?.period === period);
    if (date) {
      filtered = filtered.filter((item) =>
        dayjs(item?.createdon).isSame(dayjs(date), "day")
      );
    }
    if (roleType) filtered = filtered.filter((item) => item?.roletype === roleType);
    if (status) filtered = filtered.filter((item) => item?.appraisalstatus === status);
    setFilterList(filtered);
  }, [search, period, date, roleType, status, dataList]);

  const statusOptions = Object.entries(APPRAISAL_MYTEAM_STATUS).map(([value, meta]) => ({
    label: meta.label,
    value,
  }));

  const handleDelete = (record) => {
    Modal.confirm({
      title: "Are you sure you want to delete this appraisal?",
      icon: null,
      centered: true,
      okText: "Yes",
      cancelText: "No",
      okButtonProps: { type: "primary" },
      onOk: async () => {
        try {
          const { code, message: apiMessage } = await CALL_API(
            `performance-kpi-management/appraisal/${record.id}`,
            "delete"
          );
          if (code === STATUS_CODE.SUCCESS) {
            message.success("Appraisal deleted");
            fetchData();
          } else {
            message.error(apiMessage || "Unable to delete this appraisal");
          }
        } catch (error) {
          console.error("Error deleting appraisal:", error);
          message.error("Unable to delete this appraisal");
        }
      },
    });
  };

  // Before the appraisee has attended ("notopen"/"yettoattend"), there's
  // nothing to score or close yet - the appraiser only gets a read-only view
  // of the plan and progress so far, not the Reviewing-to-Close form.
  const navigateForRecord = (record) => {
    if (["notopen", "yettoattend"].includes(record.appraisalstatus)) {
      history.push("/performance-kpi-awaiting-response", { appraisalid: record.id });
    } else {
      history.push("/performance-kpi-review-appraisal", { appraisalid: record.id });
    }
  };

  const columns = [
    { title: "Name", dataIndex: "fullname", key: "fullname", render: (t) => t || "-" },
    { title: "Employee ID", dataIndex: "employeeid", key: "employeeid", render: (t) => t || "-" },
    { title: "Period", dataIndex: "period", key: "period", render: (t) => t || "-" },
    { title: "Created", dataIndex: "createdon", key: "createdon", render: formatDate },
    { title: "Attended on", dataIndex: "attendedon", key: "attendedon", render: formatDate },
    {
      title: (
        <Popover
          content={`((${weightage.technical} × Technical average) + (${weightage.soft} × Soft skill average)) / 100`}
        >
          Overall Average <InfoCircleFilled style={{ color: COLORS.TEXT.PRIMARY, fontSize: 12 }} />
        </Popover>
      ),
      key: "overallaverage",
      render: (_, record) => {
        if (record.appraisalstatus !== "closed") return "-";
        const averages = skillAverages[record.id];
        if (!averages) return averagesLoading ? <Spin size="small" /> : "-";
        const overall = weightedAverage(averages.technical, averages.soft, weightage);
        if (overall === null) return "-";
        return (
          <Popover
            content={
              <div>
                <div>
                  Technical average: <b>{averages.technical.toFixed(2)}</b> ({weightage.technical}%)
                </div>
                <div>
                  Soft skill average: <b>{averages.soft.toFixed(2)}</b> ({weightage.soft}%)
                </div>
              </div>
            }
          >
            <span style={{ fontWeight: 600 }}>{overall.toFixed(2)}</span>
          </Popover>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <StatusCell status={record.appraisalstatus} map={APPRAISAL_MYTEAM_STATUS} />
          {record.isdraft && <DraftBadge>Draft</DraftBadge>}
        </div>
      ),
    },
    {
      key: "actions",
      width: 160,
      render: (_, record) => (
        <MyTeamRowActions
          status={record.appraisalstatus}
          onAction={(e, action) => {
            e.stopPropagation();
            if (action === "primary") {
              navigateForRecord(record);
            } else if (action === "chart") {
              openMatrix(record.id);
            } else if (action === "delete") {
              handleDelete(record);
            } else if (action === "calendar") {
              // adjustdates operates on the KPI contract, not the appraisal
              // row itself - AdjustDatesModal expects `record.id` to be the
              // contract id it PATCHes.
              setAdjustDatesRecord({ ...record, id: record.contractid });
            }
          }}
        />
      ),
    },
  ];

  return (
    <div>
      <FilterBar>
        <div className="lite-text">List of Contract: {dataList.length}</div>
        <div className="filters">
          <Input
            placeholder="Search"
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 170 }}
          />
          <Select
            placeholder="Period"
            allowClear
            options={PERIOD_OPTIONS}
            value={period}
            onChange={setPeriod}
            style={{ width: 130 }}
          />
          <DatePicker placeholder="Date" value={date} onChange={setDate} />
          <Select
            placeholder="Role Type"
            allowClear
            value={roleType}
            onChange={setRoleType}
            style={{ width: 140 }}
          />
          <Select
            placeholder="Status"
            allowClear
            options={statusOptions}
            value={status}
            onChange={setStatus}
            style={{ width: 160 }}
          />
          <ButtonFilled type="primary" onClick={openConsolidated}>
            <BarChartOutlined /> Consolidated
          </ButtonFilled>
        </div>
      </FilterBar>
      <Spin spinning={loading}>
        <StyledTable
          columns={columns}
          dataSource={filterList}
          pagination={false}
          scroll={{ x: "max-content" }}
          rowKey={(record) => record.id}
          onRow={(record) => ({
            style: { cursor: "pointer" },
            onClick: () => navigateForRecord(record),
          })}
        />
      </Spin>

      <Modal
        title="Overall Performance Matrix"
        open={matrixOpen}
        onCancel={() => setMatrixOpen(false)}
        footer={null}
        width={900}
        centered
      >
        <Spin spinning={matrixLoading}>
          <PerformanceMatrix Xaxis={matrixAxes.x} Yaxis={matrixAxes.y} matrixdetails />
        </Spin>
      </Modal>

      <Modal
        title="Consolidated Performance Matrix of Primary Appraisees"
        open={consolidatedOpen}
        onCancel={() => setConsolidatedOpen(false)}
        footer={null}
        width={960}
        centered
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
            marginBottom: 16,
          }}
        >
          <Input
            placeholder="Search"
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            value={cSearch}
            onChange={(e) => setCSearch(e.target.value)}
            allowClear
            style={{ width: 200 }}
          />
          <Select
            placeholder="Period"
            allowClear
            options={PERIOD_OPTIONS}
            value={cPeriod}
            onChange={setCPeriod}
            style={{ width: 140 }}
          />
          <DatePicker placeholder="Date" value={cDate} onChange={setCDate} />
        </div>
        <Spin spinning={consolidatedLoading}>
          <PerformanceMatrix
            points={consolidatedFiltered}
            legendPosition="bottom"
            renderTooltip={(points) => <MatrixTooltip points={points} />}
          />
        </Spin>
      </Modal>

      <AdjustDatesModal
        open={Boolean(adjustDatesRecord)}
        onClose={() => setAdjustDatesRecord(null)}
        record={adjustDatesRecord}
        onSuccess={fetchData}
      />
    </div>
  );
};

const AppraisalList = ({ scoreWeightage }) => {
  const location = useLocation();
  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);

  // Mirrors the old Performance module's Mine/My Team permission gating -
  // reuses the same "myperformance" / "myteamperformance" menu keys so Roles
  // & Permissions doesn't need any new entries for this module. There's no
  // HR tab here (unlike the old module's "hrperformance"), so it's skipped.
  const Menulist = menuList[userRoleId] || Object.values(menuList)[0] || menuList;
  const isMine = Menulist?.find((item) => item?.key === "myperformance" && item?.permission?.view);
  const isMyteam = Menulist?.find(
    (item) => item?.key === "myteamperformance" && item?.permission?.view
  );

  const [tabId, setTabId] = useState(location.state?.subTab || (isMine ? "mine" : "myteam"));

  useEffect(() => {
    if (location.state?.subTab) setTabId(location.state.subTab);
  }, [location.state]);

  const items = [
    isMine && { key: "mine", label: "Mine", children: <MineAppraisal /> },
    isMyteam && {
      key: "myteam",
      label: "My Team",
      children: <MyTeamAppraisal scoreWeightage={scoreWeightage} />,
    },
  ].filter(Boolean);

  return <Tabs activeKey={tabId} items={items} onChange={setTabId} />;
};

export default AppraisalList;
